import { useCallback, useEffect, useState } from 'react'
import { Alert, Box, Button, Chip, CircularProgress, IconButton, Link, Tooltip, Typography } from '@mui/material'
import { OpenInNewOutlined, RefreshOutlined } from '@mui/icons-material'
import { useParams } from 'react-router-dom'
import { NewVersionDialog } from '../../../QuoteDetails/QuoteDetailsOptionsDialogs'
import { useQuoteLines } from '../../lines/QuoteLinesContext'
import { fetchOrderStatus, PortalOrderStatus } from './orderStatus'
import '../placeholder/QuoteStepPlaceholder.css'

function statusChipColor(status: string): 'success' | 'warning' | 'error' | 'info' | 'default' {
  if (status === 'Completed') return 'success'
  if (status === 'Hold - Sales' || status === 'Hold - Back Office' || status === 'Hold - SCRM') return 'warning'
  if (status === 'Rejected' || status === 'Canceled') return 'error'
  if (status === 'Submitted' || status === 'Draft') return 'info'
  return 'default'
}

const POLL_MS = 30_000

export default function OrderProcessing() {
  const { quoteId } = useParams()
  const { versions } = useQuoteLines()
  const [portal, setPortal] = useState<PortalOrderStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [newVersionOpen, setNewVersionOpen] = useState(false)

  const fetchStatus = useCallback(
    async (silent = false) => {
      if (!quoteId) return
      if (!silent) setLoading(true)
      try {
        setPortal(await fetchOrderStatus(quoteId))
        setError(false)
      } catch (err) {
        console.error(err)
        setError(true)
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [quoteId],
  )

  useEffect(() => {
    fetchStatus()
    const id = window.setInterval(() => fetchStatus(true), POLL_MS)
    return () => window.clearInterval(id)
  }, [fetchStatus])

  const status = portal?.status
  const isRejected = status === 'Rejected'
  const hasCorrection = !!portal?.hasCorrection
  const canCorrect = isRejected && !hasCorrection
  const statusLabel = isRejected && hasCorrection ? 'Rejected - Corrected' : status

  return (
    <div className="quote-step-placeholder">
      <Typography variant="h6">Won - Order Processing</Typography>
      {canCorrect ? (
        <Typography color="text.secondary">
          This order was rejected in the order portal. Create a new quote version to correct and resubmit.
        </Typography>
      ) : hasCorrection ? (
        <Typography color="text.secondary">
          This order was rejected and has already been corrected on a newer quote version.
        </Typography>
      ) : (
        <Typography color="text.secondary">
          Order submitted. Ops will continue processing in the order portal.
        </Typography>
      )}
      <Box display="flex" alignItems="center" gap={1} mt={1}>
        <Typography variant="body2" color="text.secondary">
          Current order status:
        </Typography>
        {loading && !portal ? (
          <CircularProgress size={18} />
        ) : error && !portal ? (
          <Typography variant="body2" color="error">
            Unable to load status
          </Typography>
        ) : statusLabel ? (
          <Chip label={statusLabel} size="small" color={hasCorrection ? 'warning' : statusChipColor(status ?? '')} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        )}
        <Tooltip title="Refresh status">
          <IconButton size="small" onClick={() => fetchStatus()} aria-label="Refresh portal status">
            <RefreshOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      {portal?.salesOrderNumber && (
        <Typography variant="body2" color="text.secondary">
          Sales order: {portal.salesOrderNumber}
        </Typography>
      )}
      {portal?.statusComment && (
        <Typography variant="body2" color="text.secondary">
          {portal.statusComment}
        </Typography>
      )}
      {isRejected && (portal?.rejectionCode || portal?.rejectionComments) && (
        <Alert severity="error">
          {portal.rejectionCode && (
            <Typography variant="body2" fontWeight={600}>
              {portal.rejectionCode}
            </Typography>
          )}
          {portal.rejectionComments && <Typography variant="body2">{portal.rejectionComments}</Typography>}
        </Alert>
      )}
      {canCorrect && (
        <Box>
          <Button variant="contained" onClick={() => setNewVersionOpen(true)}>
            Create new version to resubmit
          </Button>
        </Box>
      )}
      {portal?.portalUrl && (
        <Link
          href={portal.portalUrl}
          target="_blank"
          rel="noopener noreferrer"
          display="inline-flex"
          alignItems="center"
          gap={0.5}
          mt={0.5}
        >
          View in Order Portal
          <OpenInNewOutlined fontSize="inherit" />
        </Link>
      )}
      <NewVersionDialog
        open={newVersionOpen}
        onClose={() => setNewVersionOpen(false)}
        quoteId={quoteId ?? ''}
        nextVersion={versions.length + 1}
        fromOrderRejection
      />
    </div>
  )
}
